import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import 'chart.js/auto';

Chart.register(...registerables);

const MarginAndSalesTaxCalculator = () => {
  const [calculatorData, setCalculatorData] = useState({
    costPrice: '',
    sellingPrice: '',
    taxRate: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCalculatorData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateMarginAndTax = () => {
    const { costPrice, sellingPrice, taxRate } = calculatorData;

    const cost = parseFloat(costPrice);
    const sell = parseFloat(sellingPrice);
    const tax = parseFloat(taxRate);

    if (!cost || !sell || !tax) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    if (cost <= 0 || sell <= 0 || tax < 0) {
      setError('Cost price, selling price must be positive and tax rate must be non-negative');
      return;
    }

    const margin = ((sell - cost) / sell) * 100;
    const taxAmount = (sell * tax) / 100;
    const totalCost = cost + taxAmount;

    const newResults = {
      margin: margin.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      totalCost: totalCost.toFixed(2)
    };

    setResults(newResults);
  };

  const chartData = {
    labels: ['Cost Price', 'Selling Price', 'Total Cost'],
    datasets: [
      {
        label: 'Values',
        data: results ? [parseFloat(calculatorData.costPrice), parseFloat(calculatorData.sellingPrice), parseFloat(results.totalCost)] : [0, 0, 0],
        backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56'],
        borderColor: ['#FF6384', '#36A2EB', '#FFCE56'],
        borderWidth: 1
      }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-cyan-200 to-blue-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Margin and Sales Tax Calculator | Financial Analysis Tool</title>
          <meta name="description" content="Calculate profit margins and sales tax with our advanced calculator. Analyze financial metrics for business and accounting purposes." />
          <meta name="keywords" content="margin calculator, sales tax calculator, profit margin, financial analysis, business calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">💰</div>
            <h1 className="text-3xl font-bold text-gray-800">Margin and Sales Tax Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate profit margins and sales tax based on cost price, selling price, and tax rate. This calculator helps analyze financial metrics for business and accounting purposes.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Cost Price:</label>
              <input
                type="number"
                name="costPrice"
                value={calculatorData.costPrice}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter cost price"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Selling Price:</label>
              <input
                type="number"
                name="sellingPrice"
                value={calculatorData.sellingPrice}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter selling price"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Tax Rate (%):</label>
              <input
                type="number"
                name="taxRate"
                value={calculatorData.taxRate}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter tax rate"
              />
            </div>
          </div>

          <button
            onClick={calculateMarginAndTax}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Profit Margin</h3>
                  <p className="text-xl font-bold">{results.margin}%</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Sales Tax Amount</h3>
                  <p className="text-xl font-bold">${results.taxAmount}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Total Cost</h3>
                  <p className="text-xl font-bold">${results.totalCost}</p>
                </div>
              </div>
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Graph</h2>
              <Bar data={chartData} />
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
          <p className="text-gray-600 mb-8">
            To use the Margin and Sales Tax Calculator, follow these steps:
            <ol className="list-decimal ml-6">
              <li>Enter the cost price of the item.</li>
              <li>Enter the selling price of the item.</li>
              <li>Enter the tax rate as a percentage.</li>
              <li>Click the "Calculate" button to see the results.</li>
            </ol>
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="feature-item">
              <div className="feature-icon text-4xl text-green-600 mb-4">📊</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Accurate Calculations</h3>
              <p className="text-gray-600">Get precise profit margins and sales tax amounts for your financial analysis.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon text-4xl text-green-600 mb-4">💰</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Cost Analysis</h3>
              <p className="text-gray-600">Understand the total cost including tax to make informed business decisions.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon text-4xl text-green-600 mb-4">📈</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Visual Representation</h3>
              <p className="text-gray-600">View a bar graph to visually compare cost price, selling price, and total cost.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon text-4xl text-green-600 mb-4">🔍</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">User-Friendly</h3>
              <p className="text-gray-600">Easy-to-use interface with clear instructions and instant results.</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ</h2>
          <div className="faq-item mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">What is profit margin?</h3>
            <p className="text-gray-600">Profit margin is the percentage of the selling price that is turned into profit, calculated as (Selling Price - Cost Price) / Selling Price * 100.</p>
          </div>
          <div className="faq-item mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">How is sales tax calculated?</h3>
            <p className="text-gray-600">Sales tax is calculated as a percentage of the selling price, i.e., Selling Price * Tax Rate / 100.</p>
          </div>
          <div className="faq-item mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">What is total cost?</h3>
            <p className="text-gray-600">Total cost is the sum of the cost price and the sales tax amount, i.e., Cost Price + Sales Tax Amount.</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MarginAndSalesTaxCalculator;